import type { Timestamp } from 'firebase/firestore';

// Base document type with metadata
export interface BaseDocument {
  id: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
  createdBy: string;
}

// User roles
export type UserRole = 'super_admin' | 'owner' | 'admin' | 'agent' | 'viewer';

// Pricing plans
export type PricingPlan = 'free' | 'pro' | 'business' | 'enterprise';

// User profile (stored in /users collection)
export interface UserProfile extends BaseDocument {
  email: string;
  displayName: string;
  photoURL?: string;
  role: UserRole;
  organizationIds: string[];
  lastLoginAt: Timestamp;
}

// Branding/customization settings for white-labeling
export interface BrandingSettings {
  // Colors
  primaryColor: string;
  secondaryColor?: string;
  accentColor?: string;
  backgroundColor?: string;
  textColor?: string;

  // Logo
  logoUrl?: string;
  logoAltText?: string;
  faviconUrl?: string;

  // Typography
  fontFamily?: string;
  headingFontFamily?: string;

  // Portal customization
  portalTitle?: string;
  portalDescription?: string;
  welcomeMessage?: string;

  // Footer
  footerText?: string;
  showPoweredBy: boolean; // "Powered by SupportSpace" - can hide on Business+

  // Custom CSS (Enterprise only)
  customCss?: string;
}

// Organization (tenant) - stored in /organizations collection
export interface Organization extends BaseDocument {
  name: string;
  slug: string; // URL-friendly identifier for support portal (e.g., /acme-corp)
  plan: PricingPlan;
  ownerId: string;

  // Branding (for support portal customization)
  branding: BrandingSettings;

  // Billing
  stripeCustomerId?: string;
  stripeSubscriptionId?: string;
  billingEmail: string;
  billingCountry: string; // ISO country code
  isNZBased: boolean; // For GST calculation

  // Plan limits
  limits: PlanLimits;

  // Usage tracking
  usage: {
    projectCount: number;
    seatCount: number;
    articleCount: number;
    ticketCount: number;
    featureRequestCount: number;
  };

  // Settings
  settings: {
    defaultLanguage: string;
    timezone: string;
    supportEmail: string;
    autoReplyEnabled: boolean;
    autoReplyMessage?: string;
  };

  // Custom domain (Business+ plans)
  customDomain?: string;
  customDomainVerified?: boolean;
}

// Plan limits type
export interface PlanLimits {
  projects: number;
  seats: number;
  feedbackRetentionDays: number;
  knowledgeBaseArticles: number;
  fileStorageMB: number;
  monthlyTickets: number;
  customBranding: boolean;
  customDomain: boolean;
  removeWatermark: boolean;
  apiAccess: boolean;
  ssoEnabled: boolean;
  prioritySupport: boolean;
  auditLogs: boolean;
}

// Organization member
export interface OrganizationMember {
  id: string;
  organizationId: string;
  userId: string;
  email: string;
  displayName: string;
  role: Exclude<UserRole, 'super_admin'>;
  invitedAt: Timestamp;
  acceptedAt?: Timestamp;
  status: 'pending' | 'active' | 'inactive';
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

// Project (within an organization)
export interface Project extends BaseDocument {
  organizationId: string;
  name: string;
  slug: string;
  description?: string;
  domain?: string; // The domain where the widget is embedded

  // Widget settings
  widgetSettings: WidgetSettings;

  // Project-specific branding (overrides org branding)
  branding?: Partial<BrandingSettings>;

  // API key for widget authentication
  apiKey: string;

  // Feature toggles
  features: {
    feedbackEnabled: boolean;
    supportTicketsEnabled: boolean;
    featureRequestsEnabled: boolean;
    surveysEnabled: boolean;
    knowledgeBaseEnabled: boolean;
    screenshotCapture: boolean;
    sessionRecording: boolean;
  };

  // Notification settings
  notifications: {
    emailOnNewTicket: boolean;
    emailOnNewFeatureRequest: boolean;
    slackWebhookUrl?: string;
    slackChannel?: string;
  };
}

// Widget settings
export interface WidgetSettings {
  enabled: boolean;
  position: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  primaryColor: string;
  launcherIcon: 'chat' | 'help' | 'feedback' | 'custom';
  customIconUrl?: string;
  launcherText?: string;
  showKnowledgeBase: boolean;
  showFeaturePortal: boolean;
  showContactForm: boolean;
  collectEmail: boolean;
  collectName: boolean;
  requireEmail: boolean;
  triggerDelay: number; // ms before showing widget
  hideOnMobile: boolean;
  zIndex: number;
}

// Ticket types and statuses
export type TicketType = 'bug' | 'feedback' | 'feature_request' | 'support' | 'question';
export type TicketStatus = 'new' | 'open' | 'pending' | 'resolved' | 'closed';
export type TicketPriority = 'low' | 'medium' | 'high' | 'urgent';

// Ticket (feedback/support request)
export interface Ticket extends BaseDocument {
  organizationId: string;
  projectId: string;

  // Ticket number (human-readable, auto-incremented)
  ticketNumber: number;

  // Ticket details
  type: TicketType;
  status: TicketStatus;
  priority: TicketPriority;

  // Content
  subject: string;
  description: string;

  // Submitter info (the end user who submitted)
  submitterEmail: string;
  submitterName?: string;
  submitterId?: string; // If identified user

  // Session context (auto-captured by widget)
  sessionContext?: {
    url: string;
    referrer?: string;
    browser: string;
    browserVersion?: string;
    os: string;
    device: string;
    viewport: string;
    userAgent: string;
    timestamp: Timestamp;
  };

  // Console errors captured
  consoleErrors?: string[];

  // Attachments
  screenshots: string[]; // Storage URLs
  attachments: Attachment[];

  // Assignment
  assigneeId?: string;
  assigneeName?: string;

  // Tags/labels
  tags: string[];

  // Internal notes (not visible to end user)
  internalNotes?: string;

  // Resolution
  resolution?: string;
  closedAt?: Timestamp;

  // SLA tracking
  firstResponseAt?: Timestamp;
  resolvedAt?: Timestamp;

  // Rating
  satisfactionRating?: 1 | 2 | 3 | 4 | 5;
  satisfactionFeedback?: string;
}

// Attachment type
export interface Attachment {
  id: string;
  name: string;
  url: string;
  type: string;
  size: number;
  uploadedAt: Timestamp;
}

// Ticket response/reply
export interface TicketResponse extends BaseDocument {
  ticketId: string;
  organizationId: string;
  projectId: string;

  // Content
  content: string;
  attachments: Attachment[];

  // Author
  authorId: string;
  authorName: string;
  authorEmail: string;
  isAgent: boolean; // true = support agent, false = end user

  // Visibility
  isInternal: boolean; // Internal notes not shown to user

  // Email tracking
  sentViaEmail: boolean;
  emailMessageId?: string;
}

// Knowledge base article statuses
export type ArticleStatus = 'draft' | 'published' | 'archived';

// Knowledge base article
export interface Article extends BaseDocument {
  organizationId: string;
  projectId: string;

  title: string;
  slug: string;
  content: string; // Markdown
  excerpt?: string;

  // Categorization
  category: string;
  tags: string[];

  // Status
  status: ArticleStatus;
  publishedAt?: Timestamp;

  // Author
  authorId: string;
  authorName: string;

  // Analytics
  viewCount: number;
  helpfulCount: number;
  notHelpfulCount: number;

  // SEO
  metaDescription?: string;
  searchKeywords: string[];

  // Ordering
  sortOrder: number;
}

// Article category
export interface ArticleCategory extends BaseDocument {
  organizationId: string;
  projectId: string;

  name: string;
  slug: string;
  description?: string;
  icon?: string;
  sortOrder: number;
  articleCount: number;
}

// Feature request statuses
export type FeatureStatus = 'under_review' | 'planned' | 'in_progress' | 'released' | 'declined';

// Feature request
export interface FeatureRequest extends BaseDocument {
  organizationId: string;
  projectId: string;

  title: string;
  description: string;
  status: FeatureStatus;

  // Submitter
  submitterEmail: string;
  submitterName?: string;

  // Voting
  voterIds: string[]; // Array of voter identifiers (email or id)
  voteCount: number;

  // Visibility
  isPublic: boolean;

  // Timeline
  plannedRelease?: string;
  releasedAt?: Timestamp;
  releaseNotes?: string;

  // Admin notes
  internalNotes?: string;

  // Linked tickets
  linkedTicketIds: string[];

  // Categories/tags
  category?: string;
  tags: string[];
}

// Plan limits configuration
export const PLAN_LIMITS: Record<PricingPlan, PlanLimits> = {
  free: {
    projects: 1,
    seats: 2,
    feedbackRetentionDays: 30,
    knowledgeBaseArticles: 10,
    fileStorageMB: 100,
    monthlyTickets: 50,
    customBranding: false,
    customDomain: false,
    removeWatermark: false,
    apiAccess: false,
    ssoEnabled: false,
    prioritySupport: false,
    auditLogs: false,
  },
  pro: {
    projects: 5,
    seats: 10,
    feedbackRetentionDays: 365,
    knowledgeBaseArticles: 100,
    fileStorageMB: 1000,
    monthlyTickets: 500,
    customBranding: true,
    customDomain: false,
    removeWatermark: false,
    apiAccess: true,
    ssoEnabled: false,
    prioritySupport: false,
    auditLogs: false,
  },
  business: {
    projects: -1, // unlimited
    seats: 50,
    feedbackRetentionDays: -1,
    knowledgeBaseArticles: -1,
    fileStorageMB: 10000,
    monthlyTickets: -1,
    customBranding: true,
    customDomain: true,
    removeWatermark: true,
    apiAccess: true,
    ssoEnabled: true,
    prioritySupport: true,
    auditLogs: true,
  },
  enterprise: {
    projects: -1,
    seats: -1,
    feedbackRetentionDays: -1,
    knowledgeBaseArticles: -1,
    fileStorageMB: -1,
    monthlyTickets: -1,
    customBranding: true,
    customDomain: true,
    removeWatermark: true,
    apiAccess: true,
    ssoEnabled: true,
    prioritySupport: true,
    auditLogs: true,
  },
};

// Feature flags per plan
export const PLAN_FEATURES: Record<PricingPlan, string[]> = {
  free: [
    'basic_widget',
    'email_notifications',
    'basic_analytics',
  ],
  pro: [
    'basic_widget',
    'email_notifications',
    'basic_analytics',
    'custom_branding',
    'screenshot_capture',
    'knowledge_base',
    'feature_voting',
    'api_access',
    'slack_integration',
    'csv_export',
  ],
  business: [
    'basic_widget',
    'email_notifications',
    'advanced_analytics',
    'custom_branding',
    'custom_domain',
    'screenshot_capture',
    'session_recording',
    'knowledge_base',
    'feature_voting',
    'api_access',
    'slack_integration',
    'csv_export',
    'sso',
    'audit_logs',
    'priority_support',
    'remove_watermark',
    'multiple_projects',
  ],
  enterprise: [
    'basic_widget',
    'email_notifications',
    'advanced_analytics',
    'custom_branding',
    'custom_domain',
    'screenshot_capture',
    'session_recording',
    'knowledge_base',
    'feature_voting',
    'api_access',
    'slack_integration',
    'csv_export',
    'sso',
    'audit_logs',
    'priority_support',
    'remove_watermark',
    'multiple_projects',
    'custom_css',
    'dedicated_support',
    'sla_guarantee',
    'custom_integrations',
  ],
};

// Pricing (monthly, in cents USD per seat)
export const PLAN_PRICING: Record<PricingPlan, { monthly: number; annual: number }> = {
  free: { monthly: 0, annual: 0 },
  pro: { monthly: 700, annual: 500 }, // $7/mo or $5/mo annual (per seat)
  business: { monthly: 1500, annual: 1200 }, // $15/mo or $12/mo annual
  enterprise: { monthly: 0, annual: 0 }, // Custom pricing
};

// NZ GST rate
export const NZ_GST_RATE = 0.15; // 15%

// Role permissions mapping
export const ROLE_PERMISSIONS: Record<UserRole, string[]> = {
  super_admin: ['*'], // All permissions
  owner: [
    'org:manage',
    'org:billing',
    'org:members',
    'org:settings',
    'project:create',
    'project:delete',
    'project:settings',
    'ticket:view',
    'ticket:manage',
    'ticket:assign',
    'ticket:delete',
    'article:view',
    'article:create',
    'article:edit',
    'article:delete',
    'article:publish',
    'feature:view',
    'feature:manage',
    'feature:delete',
    'analytics:view',
    'analytics:export',
  ],
  admin: [
    'org:members',
    'org:settings',
    'project:settings',
    'ticket:view',
    'ticket:manage',
    'ticket:assign',
    'article:view',
    'article:create',
    'article:edit',
    'article:publish',
    'feature:view',
    'feature:manage',
    'analytics:view',
  ],
  agent: [
    'ticket:view',
    'ticket:manage',
    'ticket:assign',
    'article:view',
    'article:create',
    'article:edit',
    'feature:view',
    'analytics:view',
  ],
  viewer: [
    'ticket:view',
    'article:view',
    'feature:view',
    'analytics:view',
  ],
};

// Permission checker utility
export const hasPermission = (role: UserRole, permission: string): boolean => {
  const permissions = ROLE_PERMISSIONS[role];
  return permissions.includes('*') || permissions.includes(permission);
};

// Plan feature checker utility
export const hasPlanFeature = (plan: PricingPlan, feature: string): boolean => {
  return PLAN_FEATURES[plan].includes(feature);
};

// Plan limit checker utility
export const isWithinLimit = (
  plan: PricingPlan,
  limitType: keyof PlanLimits,
  currentCount: number
): boolean => {
  const limit = PLAN_LIMITS[plan][limitType];
  if (typeof limit === 'boolean') return limit;
  if (limit === -1) return true; // Unlimited
  return currentCount < limit;
};

// Default branding settings
export const DEFAULT_BRANDING: BrandingSettings = {
  primaryColor: '#2563eb',
  secondaryColor: '#7c3aed',
  backgroundColor: '#ffffff',
  textColor: '#1f2937',
  fontFamily: 'Inter, system-ui, sans-serif',
  portalTitle: 'Help Center',
  portalDescription: 'Find answers and get support',
  welcomeMessage: 'How can we help you today?',
  showPoweredBy: true,
};

// Default widget settings
export const DEFAULT_WIDGET_SETTINGS: WidgetSettings = {
  enabled: true,
  position: 'bottom-right',
  primaryColor: '#2563eb',
  launcherIcon: 'help',
  showKnowledgeBase: true,
  showFeaturePortal: true,
  showContactForm: true,
  collectEmail: true,
  collectName: false,
  requireEmail: true,
  triggerDelay: 0,
  hideOnMobile: false,
  zIndex: 999999,
};

// Re-export notification types
export type {
  NotificationType,
  EmailNotification,
  TicketNotificationData,
  TeamInviteNotificationData,
} from './notifications';
export { generateEmailContent, generateIdempotencyKey } from './notifications';
